import * as React from "react";
import { AppLayout } from "./AppLayout";
import { useTexts } from "../hooks/useTexts";
import styled from "styled-components";
import {
  ContentContainer,
  Card,
  InfoBox,
  SourcesSection,
  FigureCaption,
} from "./common/StyledComponents";
import { LinkifyText } from "./common/LinkifyText";
import { processInlineFormatting } from "./common/RenderText";

const illusImage = require("../../assets/kreislauf.png");

const InteractiveImageContainer = styled.div`
  position: relative;
  max-width: 100%;
  margin: 30px 0;
  text-align: center;
`;

const ImageWrapper = styled.div`
  position: relative;
  display: inline-block;
  max-width: 100%;
`;

const StyledImage = styled.img`
  max-width: 100%;
  height: auto;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  display: block;
`;

const MarkerButton = styled.button<{
  top: string;
  left: string;
  isActive: boolean;
}>`
  position: absolute;
  top: ${(props) => props.top};
  left: ${(props) => props.left};
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${(props) =>
    props.isActive ? "var(--primaryColor)" : "rgba(255, 255, 255, 0.9)"};
  color: ${(props) => (props.isActive ? "white" : "var(--primaryColor)")};
  border: 2px solid var(--primaryColor);
  font-weight: bold;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.3s ease;
  transform: translate(-50%, -50%);
  z-index: 10;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);

  &:hover {
    transform: translate(-50%, -50%) scale(1.1);
    background: var(--primaryColor);
    color: white;
  }

  @media only screen and (max-width: 768px) {
    width: 28px;
    height: 28px;
    font-size: 14px;
  }
`;

const StepItem = styled.div<{ isActive: boolean }>`
  padding: 8px 0 8px 12px;
  margin: 8px 0;
  border-left: 3px solid
    ${(props) => (props.isActive ? "var(--primaryColor)" : "transparent")};
  background: ${(props) =>
    props.isActive
      ? "rgba(var(--primaryColorRgb, 25, 118, 210), 0.06)"
      : "transparent"};
  border-radius: 0 4px 4px 0;
  transition: all 0.3s ease;

  h4 {
    color: var(--primaryColor);
    margin-bottom: 4px;
  }

  p {
    margin-bottom: 0;
    line-height: 1.6;
  }
`;

/**
 * WasserkreislaufRoute – Interaktives Wasserkreislauf-Diagramm
 */
export const WasserkreislaufRoute = React.memo(function WasserkreislaufRoute() {
  const { t } = useTexts();
  const [activeMarker, setActiveMarker] = React.useState<number | null>(null);
  const infoBoxRefs = React.useRef<(HTMLDivElement | null)[]>([]);

  const markers = [
    { id: 1, top: "60%", left: "95%" },
    { id: 2, top: "17%", left: "95%" },
    { id: 3, top: "17%", left: "45%" },
    { id: 4, top: "50%", left: "25%" },
    { id: 5, top: "60%", left: "45%" },
    { id: 6, top: "91%", left: "45%" },
    { id: 7, top: "82%", left: "81%" },
  ];

  const handleMarkerClick = (id: number) => {
    setActiveMarker(activeMarker === id ? null : id);

    // Scroll zur entsprechenden Infobox
    const infoBox = infoBoxRefs.current[id - 1];
    if (infoBox) {
      setTimeout(() => {
        infoBox.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 50);
    }
  };

  return (
    <AppLayout>
      <ContentContainer>
        <h1>{t("wasserkreislauf.title", "Der Wasserkreislauf")}</h1>

        <Card>
          <p>
            {t(
              "wasserkreislauf.intro.text1",
              "Die Erde trägt nicht umsonst den Namen 'der blaue Planet': Rund 70 % ihrer Oberfläche ist mit Wasser bedeckt. Diese Wassermassen wirken nahezu unveränderlich – das gesamte Wasserreservoir der Erde umfasst etwa 1,4 Milliarden Kubikkilometer."
            )}
          </p>
          <p>
            {t(
              "wasserkreislauf.intro.text2",
              "Der größte Teil davon ist allerdings Salzwasser in den Ozeanen. Nur etwa 2,5 % des Wassers ist Süßwasser, und davon steckt 99,7% quasi permanent in Eiskörpern und tiefen Grundwasserschichten. Von dem letzten geringen Anteil des Süßwassers, welcher im Laufe des Jahres zwischen Meer und Land, ober- und unterirdisch zirkuliert steht dem Menschen etwa 12.000 Kubikkilometer zur Verfügung."
            )}
          </p>
          <p>
            {t(
              "wasserkreislauf.intro.text3",
              "Würde diese Menge gleichmäßig auf die Weltbevölkerung verteilt werden, erhielte jede Person lediglich rund 2.000 Kubikmeter. Zum Vergleich: In Deutschland könnte eine einzelne Person mit diesem Vorrat bei einem täglichen Verbrauch von 120 Litern gerade einmal 45 Jahre lang auskommen."
            )}
          </p>
          <p>
            {t(
              "wasserkreislauf.intro.text4",
              "Das wirft eine wichtige Frage auf: Wird das Wasser auf der Erde irgendwann einfach aufgebraucht sein? Die Antwort lautet: nein. Denn Wasser wird nicht verbraucht, sondern gebraucht. Nach einem fundamentalen Gesetz der Natur kann Materie weder geschaffen noch vernichtet werden – sie verändert lediglich ihre Form. Wasser zirkuliert daher unaufhörlich im globalen Wasserkreislauf: Es verdunstet, kondensiert, fällt als Regen oder Schnee zurück auf die Erde und gelangt schließlich über Flüsse und Grundwasser wieder in die Ozeane."
            )}
          </p>
        </Card>

        <Card>
          <h2>
            {t(
              "wasserkreislauf.diagram.title",
              "Der Wasserkreislauf im Detail"
            )}
          </h2>
          <p>
            {t(
              "wasserkreislauf.diagram.intro",
              "Klicken Sie auf die Punkte im Bild, um mehr über die einzelnen Schritte des Wasserkreislaufs zu erfahren:"
            )}
          </p>

          <InteractiveImageContainer>
            <ImageWrapper>
              <StyledImage src={illusImage} alt="Der Wasserkreislauf" />
              {markers.map((marker) => (
                <MarkerButton
                  key={marker.id}
                  top={marker.top}
                  left={marker.left}
                  isActive={activeMarker === marker.id}
                  onClick={() => handleMarkerClick(marker.id)}
                  aria-label={`Punkt ${marker.id}`}
                >
                  {marker.id}
                </MarkerButton>
              ))}
            </ImageWrapper>
            <FigureCaption>
              Abbildung 1:{" "}
              {t(
                "wasserkreislauf.diagram.caption",
                "Positionierungshinweis für die Infoboxen [eigene Darstellung]"
              )}
            </FigureCaption>
          </InteractiveImageContainer>

          <StepItem
            ref={(el) => (infoBoxRefs.current[0] = el)}
            isActive={activeMarker === 1}
          >
            <h4>1. {t("wasserkreislauf.infobox1.title", "Verdunstung")}</h4>
            <p>
              {t(
                "wasserkreislauf.infobox1.text",
                "Die Wärmestrahlung der Sonne lässt ständig Wasser in die Atmosphäre aufsteigen (Verdunstung). Die gelösten Bestandteile - vor allem Kochsalz - bleiben zurück. Über Millionen Jahre hat sich Salz im Meer angereichert."
              )}
            </p>
          </StepItem>

          <StepItem
            ref={(el) => (infoBoxRefs.current[1] = el)}
            isActive={activeMarker === 2}
          >
            <h4>2. {t("wasserkreislauf.infobox2.title", "Kondensation")}</h4>
            <p>
              {t(
                "wasserkreislauf.infobox2.text",
                "Wasserdampf kondensiert in kälteren Höhen zu Wolken."
              )}
            </p>
          </StepItem>

          <StepItem
            ref={(el) => (infoBoxRefs.current[2] = el)}
            isActive={activeMarker === 3}
          >
            <h4>3. {t("wasserkreislauf.infobox3.title", "Niederschlag")}</h4>
            <p>
              {t(
                "wasserkreislauf.infobox3.text",
                "Als süßes Regenwasser oder auch als Schnee gelangt das Wasser auf das Festland."
              )}
            </p>
          </StepItem>

          <StepItem
            ref={(el) => (infoBoxRefs.current[3] = el)}
            isActive={activeMarker === 4}
          >
            <h4>
              4.{" "}
              {t("wasserkreislauf.infobox4.title", "Verdunstung über Pflanzen")}
            </h4>
            <p>
              {t(
                "wasserkreislauf.infobox4.text",
                "Der Niederschlag wässert die Pflanzen und verdunstet teilweise wieder."
              )}
            </p>
          </StepItem>

          <StepItem
            ref={(el) => (infoBoxRefs.current[4] = el)}
            isActive={activeMarker === 5}
          >
            <h4>5. {t("wasserkreislauf.infobox5.title", "Fließgewässer")}</h4>
            <p>
              {t(
                "wasserkreislauf.infobox5.text",
                "Der Niederschlag füllt Becken, speist die Quellen der Bäche, lässt Flüsse fließen. Das Wasser 'vergisst' nicht, wo es schon einmal war. Denn der Durchfluss durch das Land löst verschiedene Mineralien aus dem Boden. Wasser unterschiedlicher Zusammensetzung entsteht, hartes oder auch weiches Wasser, mineralisch schmackhaftes, eisenhaltiges oder salziges Wasser, aber auch welches, das giftige Salze wie Natriumfluorid oder Schwermetalle aus dem Boden gewaschen hat."
              )}
            </p>
          </StepItem>

          <StepItem
            ref={(el) => (infoBoxRefs.current[5] = el)}
            isActive={activeMarker === 6}
          >
            <h4>6. {t("wasserkreislauf.infobox6.title", "Versickerung")}</h4>
            <p>
              {t(
                "wasserkreislauf.infobox6.text",
                "Ein Teil des Niederschlags versickert im Boden und speist den unterirdischen Wasservorrat."
              )}
            </p>
          </StepItem>

          <StepItem
            ref={(el) => (infoBoxRefs.current[6] = el)}
            isActive={activeMarker === 7}
          >
            <h4>
              7. {t("wasserkreislauf.infobox7.title", "Rückfluss zum Meer")}
            </h4>
            <p>
              {t(
                "wasserkreislauf.infobox7.text",
                "Flüsse führen das Wasser zurück zum Meer, womit der Kreislauf sich schließt."
              )}
            </p>
          </StepItem>
        </Card>

        <Card>
          <p>
            {t(
              "wasserkreislauf.outro.text1",
              "Das bedeutet auch: Das Wasser, das du heute trinkst, verlässt irgendwann deinen Körper – und setzt seinen Weg im großen Kreislauf der Natur fort."
            )}
          </p>
          <InfoBox>
            <p>
              {t(
                "wasserkreislauf.outro.link1",
                "Warum wir dennoch von Wasserknappheit sprechen müssen, erfährst du hier (Kapitel: Wasserknappheit)."
              )}
            </p>
            <p>
              {t(
                "wasserkreislauf.outro.link2",
                "Wenn ihr wissen wollt, wie der Mensch sich selbst in diesen Kreislauf eingebunden hat, klicke hier (Kapitel: Wasserdargebot und Wasserverbrauch)."
              )}
            </p>
          </InfoBox>
        </Card>

        <SourcesSection>
          <h3>{t("wasserkreislauf.literatur.title", "Literatur")}</h3>
          <ol>
            <li id="quelle-1">
              <LinkifyText>
                {t(
                  "wasserkreislauf.literatur.ref1",
                  "H. Bartel, H. H. Dieter, I. Feuerpfeil, H-J. Grummt, T. Grummt, A. Hummel, et al., 'Rund um das Trinkwasser', Umweltbundesamt, 2016. [Online]. Verfügbar unter: https://www.umweltbundesamt.de/sites/default/files/medien/479/publikationen/uba_rund_um_das_trinkwasser_ratgeber_web_0.pdf"
                ).replace(/^\[\d+\]\s*/, "")}
              </LinkifyText>
            </li>
            <li id="quelle-2">
              <LinkifyText>
                {t(
                  "wasserkreislauf.literatur.ref2",
                  "Heinrich-Böll-Stiftung und Bund für Umwelt und Naturschutz Deutschland, Hrsg., Wasseratlas: Daten und Fakten über die Grundlage allen Lebens, 1. Auflage. Paderborn: Bonifatius Druck, 2025."
                ).replace(/^\[\d+\]\s*/, "")}
              </LinkifyText>
            </li>
          </ol>
        </SourcesSection>
      </ContentContainer>
    </AppLayout>
  );
});
